import axios, { AxiosInstance } from 'axios'
import config from '../../utils/config'
import { ApplicationInsights } from '@microsoft/applicationinsights-web'

// Get the App Insights instance from the window if available
const getAppInsights = (): ApplicationInsights | null => {
  return (window as any).__appInsights || null;
}

/**
 * Create and configure an Axios instance for API requests
 */
const createAPIClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: config.api.baseUrl,
    timeout: 300000, // 5 minutes for file uploads
    headers: {
      'Content-Type': 'application/json',
    },
  })

  // Request interceptor for adding auth if enabled
  client.interceptors.request.use((config) => {
    // Attach AuthGuard token if present
    const token = localStorage.getItem('authguard_access_token')
    if (token) {
      (config.headers as any).Authorization = `Bearer ${token}`
    }
    return config
  })

  // Response interceptor for handling common errors
  client.interceptors.response.use(
    (response) => response,
    (error) => {
      // Detailed error logging
      if (error.response) {
        // Server responded with an error
        const { status, data, config } = error.response;
        const url = config.url;
        const method = config.method?.toUpperCase();
        
        // Log to console
        console.error('API error:', status, data);
        
        // Log to Application Insights with detailed properties
        const appInsights = getAppInsights();
        if (appInsights) {
          const errorProperties = {
            statusCode: status,
            endpoint: url,
            method: method,
            requestBody: JSON.stringify(config.data || {}),
            responseBody: JSON.stringify(data || {}),
            errorMessage: data.detail || (typeof data === 'string' ? data : 'Unknown error')
          };
          
          // Track as a dependency failure with details
          appInsights.trackDependencyData({
            id: `${method}-${url}-${Date.now()}`,
            name: `${method} ${url}`,
            duration: error.duration || 0,
            success: false,
            responseCode: status,
            properties: errorProperties
          });
          
          // For 400 errors, also track as an exception for better visibility
          if (status === 400) {
            const errorMessage = data.detail || 'Bad Request';
            appInsights.trackException({
              exception: new Error(`API 400 Error: ${errorMessage}`),
              properties: {
                ...errorProperties,
                rawResponse: JSON.stringify(data)
              }
            });
            
            // Additional custom event for debugging
            appInsights.trackEvent({
              name: 'API_BadRequest_Details',
              properties: {
                ...errorProperties,
                clientId: new URLSearchParams(url.split('?')[1] || '').get('client_id'),
                timestamp: new Date().toISOString()
              }
            });
          }
        }
        
        // Handle authentication errors
        if (status === 401) {
          // In a real app, we would redirect to login or refresh token
          console.warn('Authentication required')
        }
      } else if (error.request) {
        // Request was made but no response received
        console.error('API connection error - no response received')
        
        // Log to Application Insights
        const appInsights = getAppInsights();
        if (appInsights) {
          appInsights.trackException({
            exception: new Error('API connection error - no response received'),
            properties: {
              url: error.config?.url,
              method: error.config?.method?.toUpperCase()
            }
          });
        }
      } else {
        // Error setting up the request
        console.error('API request error:', error.message)
        
        // Log to Application Insights
        const appInsights = getAppInsights();
        if (appInsights) {
          appInsights.trackException({
            exception: error,
            properties: {
              message: error.message
            }
          });
        }
      }
      
      return Promise.reject(error)
    }
  )

  return client
}

// Singleton API client instance
export const apiClient: AxiosInstance = createAPIClient()

/**
 * API error handling utility
 */
export const handleAPIError = (error: any): string => {
  if (error.response) {
    // The server responded with an error
    const { status, data } = error.response
    
    if (status === 400) {
      // Bad request errors usually have more specific error messages
      if (data.detail) {
        return Array.isArray(data.detail) 
          ? data.detail.map((err: any) => err.msg).join('. ')
          : data.detail
      }
      return 'Invalid request. Please check your input.'
    }
    
    if (status === 401) {
      return 'Authentication required. Please log in.'
    }
    
    if (status === 403) {
      return 'You don\'t have permission to perform this action.'
    }
    
    if (status === 404) {
      return 'The requested resource was not found.'
    }
    
    if (status === 429) {
      // For exponential backoff, use the detailed backend message if available
      return data.detail || 'Rate limit exceeded. Please try again later.'
    }
    
    if (status >= 500) {
      return 'Server error. Please try again later.'
    }
    
    return `Error: ${data.detail || 'Something went wrong'}`
  }
  
  if (error.request) {
    // The request was made but no response was received
    return 'Cannot connect to server. Please check your internet connection.'
  }
  
  // Something happened in setting up the request
  return error.message || 'An unknown error occurred'
}

export default apiClient